import React, { useCallback, useEffect, useState, useRef } from 'react';
import { cancellablePromise, useCancellablePromise } from '../ComplexDocumentDesign/cancellablePromise';
import { Tooltip, Icon } from 'antd';
import { useDocumentContext, useComplexDocumentHistory, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../context/complexDocumentContext';
import { ActionButton, SavedIcon, OpenCloudIcon } from '../ComplexDocumentDesign/styles';
import { formDocumentPayloadFromChanges } from '../ComplexDocumentDesign/complexDocumentHelper';
import { COMPLEX_DOCUMENT_VERSION } from '../../utils/complexDocumentConstants';
import { useArtboardContext, CONTEXT_KEYS } from '../../context/complexArtboardContext';
import { patchComplexEmail, patchComplexEmailBlock } from '../../utils/services/complexEmailAPI';
import { useDefaultStyleContext } from '../../context/complexDefaultStyleContext';
import { languageUpdateHandler } from '../../utils/detectLanguageHelper';
import { useSessionContext } from '../../context/sessionContext';

const EmailAutoSaveButton = ({ editable, noInternetTooltip }) => {

  const documentContextRef = useRef(null);
  const documentStyleContextRef = useRef(null);
  const draftUpdate = useRef(null);
  const waitingForResponse = useRef(false);
  const changesProcessed = useRef(0);
  const [isOnline, setIsOnline] = useState(window.navigator.onLine);

  const organisationSelector = useCallback(store => store.organisationDetails, []);
  const [organisationDetails] = useSessionContext(organisationSelector);

  const customSelector = useCallback((store) => {
    documentContextRef.current = store;
    return null;
  }, []);
  const [, store] = useDocumentContext(customSelector, `EmailAutoSaveButton`);
  const getDefaultStyleSelector = useCallback(store => {
    documentStyleContextRef.current = store;
    return null;
  }, []);
  useDefaultStyleContext(getDefaultStyleSelector);
  
  const currentDesignSelector = useCallback((store) => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign] = useArtboardContext(currentDesignSelector, '');

  const lifecycleSelector = useCallback(store => store[CONTEXT_KEYS.LIFECYCLE_TIMELINE], []);
  const [lifeCycle, updateArtboard] = useArtboardContext(lifecycleSelector, `ComplexDocumentName`);

  const customCurrentBlockSelector = useCallback((store) => store[CONTEXT_KEYS.CURRENT_BLOCK], []);
  const [currentEditingBlock] = useArtboardContext(customCurrentBlockSelector);

  const currentActionselector = useCallback((store) => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_DRAG_ACTION], []);
  const [dragAction] = useDocumentContext(currentActionselector, '');

  const [history, historyStore] = useComplexDocumentHistory(store => store.history);
  const [loading, setLoading] = useState(false);
  const hasUnsavedChanges = useRef(false);
  const { addCancellablePromise, removePendingPromise, clearPendingPromises } = useCancellablePromise();
  const setIsSavingChanges = (value) => {
    store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_SAVING_CHANGES]: value });
  }

  const autoSaveChanges = useCallback(async (allChanges) => {
    if (waitingForResponse.current) return { status: 'failure', count: 0 };
    clearPendingPromises();
    const cancellablePromise1 = cancellablePromise(new Promise(resolve => setTimeout(resolve, 1000)));
    addCancellablePromise(cancellablePromise1);
    try {
      await cancellablePromise1.promise;
      removePendingPromise(cancellablePromise1);
      waitingForResponse.current = true;
      const historyLength = allChanges.length;
      const mergedChanges = historyStore.mergeChanges(allChanges);
      console.log({ mergedChanges });
      let response;
      if (mergedChanges.length > 0) {
        let payload;
        try {
           payload = formDocumentPayloadFromChanges(documentContextRef.current, mergedChanges ,currentDesign, documentStyleContextRef.current, true);
        } catch(err) {
          console.log("Error in payload formation :: ", err);
          return;
        }
        console.log({ payload, currentDesign });
        try {
          if(currentDesign === 'document') {
            const refFlowUuid =   documentContextRef.current.refrenced_flow_uuid;
            response = await patchComplexEmail(payload, refFlowUuid);
          } else if(currentDesign === 'block') {
            response = await patchComplexEmailBlock(payload);
          }
        } catch (err) {
          console.log("Error in persisting changes for offline storage :: ", err);
        }
        console.log({ response });
        let changes = documentContextRef.current[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS];
        let hasChange = false;
        if (response?.flows?.length) {
          response.flows.forEach(savedFlow => {
            if(savedFlow.isStandalone &&  currentDesign === 'document' ) return;
            // Syncing only when the ID has not been persisted yet, not expecting any udpates
            if (changes[savedFlow.uuid] === undefined && currentDesign !== 'flow') {
              hasChange = true;
              changes = Object.assign({}, changes, {
                [savedFlow.uuid]: savedFlow.flowId
              });
            }
            if (savedFlow.header && changes[savedFlow.header.uuid] === undefined) {
              hasChange = true;
              changes = Object.assign({}, changes, {
                [savedFlow.header.uuid]: savedFlow.header.blockId
              });
            }
            if (savedFlow.footer && changes[savedFlow.footer.uuid] === undefined) {
              hasChange = true;
              changes = Object.assign({}, changes, {
                [savedFlow.footer.uuid]: savedFlow.footer.blockId
              });
            }
            // TODO : Sync block IDs from response
            let blocksToSync = savedFlow.blocks.filter(block => {
              if (block.blockDTOList !== undefined) {
                if (changes[block.uuid] === undefined) return true;
                return block.blockDTOList.find(childBlock => changes[childBlock.uuid] === undefined) !== undefined;
              }
              return changes[block.uuid] === undefined;
            });
            if (blocksToSync.length > 0) {
              hasChange = true;
              changes = Object.assign({}, changes, blocksToSync.reduce((acc, block) => {
                let childBlockChanges = {};
                if (block.blockDTOList) {
                  childBlockChanges = block.blockDTOList.reduce((acc1, childBlock) => {
                    return Object.assign({}, acc1, {
                      [childBlock.uuid]: childBlock.blockId
                    });
                  }, {});
                }
                return Object.assign({}, acc, childBlockChanges, {
                  [block.uuid]: block.blockId
                });
              }, {}))
            }
          });
          setLoading(false);
          setIsOnline(true);
          setIsSavingChanges(false);
        } else {
          // TODO : WHETHER TO ALERT USER / HANDLE OFFLINE CHANGES
          // TODO : Check if navigator is online, if no, change the class of auto save button
          if (response === undefined) {
            if (!window.navigator.onLine) {
              setLoading(false);
              setIsOnline(false);
              setIsSavingChanges(false);
              // TODO : Add listener to retry if user does not continue editing
            } else {
              // TODO : Possibility of backend Server down
              setLoading(true);
              setIsSavingChanges(true);
              // TODO : Stop designing
              let payloadDetails = {
                payload,
                dataTime: (new Date()).toLocaleString()
              }
              sessionStorage.setItem('lastUnsuccessfulPayload', JSON.stringify(payloadDetails));
              updateArtboard({
                [CONTEXT_KEYS.BLOCK_DESIGN]: {
                  code: '<SER_ERR>'
                }
              });
            }
          } else if (response?.documentId) {
            setLoading(false);
            setIsOnline(true);
            setIsSavingChanges(false);
          } else {
            // TODO : Possibility of a data error? response is obtained from backend but has a different status code that is not success
            setLoading(true);
            setIsSavingChanges(true);
            console.log("Error in backend response for saving changes :: ", response);
            // TODO : Stop designing
            let payloadDetails = {
              payload,
              dataTime: (new Date()).toLocaleString()
            }
            sessionStorage.setItem('lastUnsuccessfulPayload', JSON.stringify(payloadDetails));
            updateArtboard({
              [CONTEXT_KEYS.BLOCK_DESIGN]: {
                code: '<SER_DATA_ERR>'
              }
            });
          }
        }
        console.log({ changes, hasChange }); // Many a times there might not be any changes
        if (hasChange) {
          // Only instance calling Change in document context directly from component
          store.set({
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS]: changes
          });
          console.log("Change in document context from save :: ", store.get());
        }
      } else { // Otherwise no changes
        console.log("No merged changes!! ");
        setLoading(false);
        setIsOnline(true);
        setIsSavingChanges(false);
      }
      waitingForResponse.current = false;
      console.log({ response });
      return response?.flows ? { status: 'success', count: historyLength } : { status: 'failure', count: historyLength }; // Do not clear history when valid response is not received
    } catch (err) {
      removePendingPromise(cancellablePromise1);
      return { status: 'failure', count: 0 };
    }
    return { status: 'no promise', count: 0 };
  }, [currentDesign]);

  useEffect(() => {
    if (history.length === 0) return;
    setLoading(true);
    setIsSavingChanges(true);
    const tryAutoSave = async (allChanges) => {
      const autosaveCallback = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.AUTOSAVE_CALLBACK];
      const result = await autoSaveChanges(allChanges);
      if (result.status === 'success') {
        draftUpdate.current = true;
        changesProcessed.current += result.count;
        console.log("Successfully saved changes :: " , result, changesProcessed.current);
        if (hasUnsavedChanges.current) {
          hasUnsavedChanges.current = false;
        }
        // This will trigger the useEffect again if there are pending changes
        historyStore.set({ action: 'clear', changes: { count: result.count } });
        autosaveCallback && autosaveCallback(result.status);
        !currentEditingBlock && dragAction && languageUpdateHandler(store, 'email', organisationDetails);
      } else {
        hasUnsavedChanges.current = allChanges;
        console.log("failure in saving changes :: ", result);
      }
      if (autosaveCallback) {
        store.set({[COMPLEX_DOCUMENT_CONTEXT_KEYS.AUTOSAVE_CALLBACK]: undefined});
      }
    }
    tryAutoSave(history);
  }, [history]);

  useEffect(() => {
    if (!isOnline) {
      const tryAutoSave = async (allChanges) => {
        const result = await autoSaveChanges(allChanges);
        if (result.status === 'success') {
          draftUpdate.current = true;
          changesProcessed.current += result.count;
          console.log("Successfully saved changes :: " , result, changesProcessed.current);
          if (hasUnsavedChanges.current) {
            hasUnsavedChanges.current = false;
          }
          // This will trigger the useEffect again if there are pending changes
          historyStore.set({ action: 'clear', changes: { count: result.count } });
        } else {
          hasUnsavedChanges.current = allChanges;
          console.log("failure in saving changes :: ", result);
        }
      };
      const onlineHandler = () => {
        setIsOnline(true);
        if (hasUnsavedChanges.current !== false && hasUnsavedChanges.current.length > 0) {
          setLoading(true);
          setIsSavingChanges(true);
          tryAutoSave([...hasUnsavedChanges.current]);
        }
      }
      window.addEventListener('online', onlineHandler);
      return () => {
        window.removeEventListener('online', onlineHandler);
      }
    }
  }, [isOnline])

  useEffect(() => {
    if (draftUpdate.current) {
      updateArtboard({
        [CONTEXT_KEYS.LIFECYCLE_TIMELINE]: Object.assign({}, lifeCycle, {
          [COMPLEX_DOCUMENT_VERSION.DRAFT]: new Date()
        })
      });
      draftUpdate.current = false;
    }
  }, [history, lifeCycle]);

  return (
    <Tooltip
      placement="bottom"
      visible={!isOnline}
      title={noInternetTooltip}
      overlayClassName={'blockingAutoSave'}
    >
    <ActionButton disabled={!editable}>
       {loading ? <Icon type="cloud-sync" style={{ fontSize: 24 }} /> : isOnline ? <SavedIcon /> : (
         <i aria-label="icon: cloud-sync" className="anticon" style={{ fontSize: 24 }}>
           <OpenCloudIcon />
         </i>
        )}
    </ActionButton>
    </Tooltip>
  )
}

export default EmailAutoSaveButton;